import gql from 'graphql-tag';
import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import useSnackbar from '@watershed/shared-frontend/hooks/useSnackbar';
import { getGqlResultData } from '@watershed/shared-frontend/utils/errorUtils';
import must from '@watershed/shared-universal/utils/must';
import { TestIds } from '@watershed/shared-universal/utils/testUtils';
import Button from '@watershed/ui-core/components/Button';
import CopyButton from '@watershed/ui-core/components/CopyButton';
import { AutocompleteFieldNonFormik } from '@watershed/ui-core/components/Form/AutocompleteField';
import {
  TextFieldMultilineNonFormik,
  TextFieldNonFormik,
} from '@watershed/ui-core/components/Form/TextField';
import ProgressButton from '@watershed/ui-core/components/ProgressButton';
import StepDialog, {
  StepDialogStep,
} from '@watershed/ui-core/components/StepDialog';

import { GQCreateApiKeyInput } from '../../../generated/graphql-schema-types';
import {
  useAllRolesQuery,
  useCreateApiKeyMutation,
} from '../../../generated/urql';
import ErrorPage from '../../ErrorPage';

gql`
  mutation CreateApiKey($input: CreateApiKeyInput!)
  @withOwner(owner: EnterpriseFoundations) {
    createApiKey(input: $input) {
      key {
        id
        name
        description
        createdAt
        updatedAt
        roles {
          id
          name
        }
      }
      secret
    }
  }
`;

export default function CreateApiKeyStepDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useLingui();
  const [submitting, setSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [secret, setSecret] = useState<string | null>(null);
  const onNext = () => setCurrentStep((curr) => curr + 1);
  const [createInput, setCreateInput] = useState<GQCreateApiKeyInput>({
    name: '',
    description: '',
    roleIds: [],
  });
  const [, executeCreate] = useCreateApiKeyMutation();
  const resetCreateInput = () =>
    setCreateInput({
      name: '',
      description: '',
      roleIds: [],
    });
  const resetSecret = () => setSecret(null);
  const isSubmitDisabled =
    createInput.name === '' || createInput.roleIds.length === 0;
  const { enqueueSnackbar } = useSnackbar();

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      resetCreateInput();
      resetSecret();
      setCurrentStep(1);
    }, 500);
  };

  const [rolesResult] = useAllRolesQuery();

  if (rolesResult.error) {
    return <ErrorPage error={rolesResult.error} />;
  }

  const rolesData = getGqlResultData(rolesResult);
  const roles = (rolesData?.allRoles ?? []).filter(
    (role) => role.workosGroupId === null
  ); // don't allow api keys to be assigned to SCIM roles

  const steps: Array<StepDialogStep> = [
    {
      children: (
        <Stack gap={3}>
          <TextFieldNonFormik
            id="name"
            data-testid={TestIds.ApiSettingsDialogNameField}
            label={t({ message: 'Name', context: 'form field label' })}
            required
            onChange={(e) =>
              setCreateInput({ ...createInput, name: e.target.value })
            }
          />
          <TextFieldMultilineNonFormik
            rows={4}
            data-testid={TestIds.ApiSettingsDialogDescriptionField}
            id="description"
            label={
              <Typography>
                <Trans context="form field label">
                  Description
                  <Box component="span" sx={{ color: 'grey50' }}>
                    —optional
                  </Box>
                </Trans>
              </Typography>
            }
            onChange={(e) =>
              setCreateInput({ ...createInput, description: e.target.value })
            }
          />
          <AutocompleteFieldNonFormik
            label={t({ message: 'Roles', context: 'form field label' })}
            sublabel={t({
              message:
                'Select the roles that this API key will be connected to. API keys cannot be connected to SCIM roles.',
              context: 'form field sublabel',
            })}
            data-testid={TestIds.ApiSettingsDialogRolesField}
            id="roleIds"
            options={roles.map((r) => r.id)}
            getOptionLabel={(roleId) =>
              must(roles.find((r) => r.id === roleId)).name
            }
            multiple
            required
            onChange={(_, value) =>
              setCreateInput({
                ...createInput,
                roleIds: value.map((v) =>
                  typeof v === 'string' ? v : (v as { value: string }).value
                ) as Array<string>,
              })
            }
          />
        </Stack>
      ),
      dialogProps: {
        header: {
          title: t({ message: 'Create API key', context: 'form title' }),
        },
        actions: (
          <ProgressButton
            data-testid={TestIds.ApiSettingsDialogCreateButton}
            onClick={async () => {
              setSubmitting(true);
              const result = await executeCreate({
                input: createInput,
              });
              if (result.error || !result.data?.createApiKey.secret) {
                enqueueSnackbar('Error creating API key.', {
                  variant: 'error',
                });
                setSubmitting(false);
                return;
              }

              setSubmitting(false);
              setSecret(result.data.createApiKey.secret);
              onNext();
              resetCreateInput();
            }}
            isInProgress={submitting}
            disabled={isSubmitDisabled}
            tooltip={
              isSubmitDisabled
                ? t({
                    message: 'Please fill out all fields',
                    context: 'form tooltip',
                  })
                : undefined
            }
          >
            <Trans context="Button copy">Create API key</Trans>
          </ProgressButton>
        ),
      },
    },
    {
      children: (
        <Stack gap={2}>
          <Typography>
            <Trans context="Create API key step dialog">
              Please save your secret key in a safe place. Keep it secure;
              anyone with your API key can make requests on your behalf.
            </Trans>
          </Typography>
          <Stack
            direction="row"
            sx={{
              gap: 1,
              alignItems: 'flex-end',
              width: '100%',
            }}
          >
            <TextFieldNonFormik
              data-testid={TestIds.ApiSettingsDialogSecretField}
              id="secret"
              sx={{ flexGrow: 1 }}
              label={
                <Typography variant="h4">
                  <Trans context="form field label">Secret</Trans>
                </Typography>
              }
              value={secret ?? ''}
              // @ts-expect-error - InputProps is valid for TextFieldNonFormik
              InputProps={{
                readOnly: true,
              }}
            />
            <CopyButton
              text={secret ?? ''}
              iconOnly
              color="primary"
              size="large"
            />
          </Stack>
        </Stack>
      ),
      dialogProps: {
        header: {
          title: t({ message: 'Save your secret', context: 'form title' }),
          hideClose: true,
        },
        actions: (
          <Button
            onClick={handleClose}
            color="primary"
            data-testid={TestIds.ApiSettingsDialogDoneButton}
          >
            <Trans context="Button copy">Done</Trans>
          </Button>
        ),
      },
    },
  ];

  return (
    <StepDialog
      stepCount={steps.length}
      currentStep={currentStep}
      steps={steps}
      onStepChange={setCurrentStep}
      defaultDialogProps={{
        open,
        onClose: handleClose,
      }}
      // we disable step change because going backwards is weird
      disableStepChange
    />
  );
}
